import { computed } from 'vue'
import { useRoute } from 'vue-router'
import usePermissionStore from '@/store/modules/permission'
import { translatePageText } from '@/locales/pageText'
import { checkPermi } from '@/utils/permission'

// Presentation copies of the existing permission-filtered tree. Never rewrite route records.
function absolutePath(parent: string, path: string): string {
  if (/^(https?:|mailto:|tel:)/.test(path) || path.startsWith('/')) return path
  return `${parent}/${path}`.replace(/\/+/g, '/')
}
function normalize(items: any[], parent = ''): any[] {
  return (items || []).map(item => {
    const path = absolutePath(parent, item.path || '')
    const children = normalize(item.children, path)
    return { ...item, path, children, alwaysShow: children.some(child => !child.hidden) || item.alwaysShow }
  })
}
export function firstMenuPage(menu: any): any {
  if (!menu || menu.hidden) return undefined
  const children = (menu.children || []).filter((child: any) => !child.hidden)
  if (!children.length) return menu
  for (const child of children) {
    const page = firstMenuPage(child)
    if (page) return page
  }
}
// Detail ownership is independent of browser history and backend menu directory names.
export function accountDetailNavigation(path: string) {
  if (/^\/user-profiles\/detail(?:\/|$)/.test(path)) {
    return { tab: 'userProfiles', category: 'consumer', listTitle: 'Domi用户', detailTitle: '用户详情' }
  }
  if (/^\/teachers-and-students\/teachers\/detail\/?$/.test(path)) {
    return { tab: 'teachers', category: 'teaching', listTitle: '老师账号', detailTitle: '老师详情' }
  }
  if (/^\/teachers-and-students\/students\/detail\/?$/.test(path)) {
    return { tab: 'studentAccounts', category: 'teaching', listTitle: '学员账号', detailTitle: '学员详情' }
  }
}

export function useMixedNavigation() {
  const route = useRoute()
  const permission = usePermissionStore()
  const modules = computed(() => {
    const visibleModules = normalize(permission.sidebarRouters).filter(item => !item.hidden).flatMap(item => {
      // The static home route is a structural Layout wrapper, not a named business module.
      return item.path === '/' && !item.meta?.title ? item.children.filter((child: any) => !child.hidden) : [item]
    })
    // Academic affairs is a navigation group, not a new registered page route.
    // Backend directory paths can differ from their absolute child page paths.
    // Locate the actual classroom page instead of assuming its parent directory URL.
    function findVisiblePage(menu: any, path: string): any {
      if (!menu || menu.hidden) return undefined
      if (menu.path.replace(/\/$/, '') === path) return menu
      for (const child of menu.children || []) {
        const page = findVisiblePage(child, path)
        if (page) return page
      }
    }
    const classroomPath = '/classroom-management/classroom'
    const classroom = visibleModules.find(item => findVisiblePage(item, classroomPath))
    const equipment = visibleModules.find(item => item.path === '/equipment')
    const courseRecordsPath = '/cr/course-records'
    const courseRecords = visibleModules.find(item => findVisiblePage(item, courseRecordsPath))
    const academicChildren: any[] = []
    const classroomPage = findVisiblePage(classroom, classroomPath)
    if (classroomPage) academicChildren.push({ ...classroomPage, alwaysShow: false, children: [] })
    const equipmentPages = (equipment?.children || []).filter((item: any) => !item.hidden)
      .map((item: any) => item.path === '/equipment/3d' ? {
        ...item,
        meta: { ...item.meta, title: translatePageText('3D设备'), titleKey: undefined }
      } : item)
    if (equipment && equipmentPages.length) {
      academicChildren.push({ ...equipment, alwaysShow: true, children: equipmentPages })
    }
    const courseRecordsPage = findVisiblePage(courseRecords, courseRecordsPath)
    if (courseRecordsPage) {
      academicChildren.push({
        ...courseRecordsPage,
        meta: { ...courseRecordsPage.meta, title: translatePageText('课程记录'), titleKey: undefined },
        alwaysShow: false,
        children: []
      })
    }
    if (academicChildren.length) {
      const academicModules = new Set([classroom, equipment, courseRecords].filter(Boolean))
      const firstIndex = visibleModules.findIndex(item => academicModules.has(item))
      const remaining = visibleModules.filter(item => !academicModules.has(item))
      remaining.splice(firstIndex, 0, {
        path: '/__academic-affairs-menu',
        meta: { title: translatePageText('教务'), icon: 'lucide-graduation-cap' },
        alwaysShow: true,
        children: academicChildren
      })
      visibleModules.splice(0, visibleModules.length, ...remaining)
    }
    // Group existing permitted pages for presentation; keep their URLs and route records.
    const orderPaths = ['/organization-orders', '/course-package']
    const orderModules = orderPaths.map(path => visibleModules.find(item => item.path === path)).filter(Boolean)
    if (!orderModules.length) return visibleModules
    const children = orderModules.map(menu => firstMenuPage(menu)).filter(Boolean)
      .map(page => ({ ...page, alwaysShow: false, children: [] }))
    if (!children.length) return visibleModules
    const group = {
      path: '/__orders-menu', // Navigation index only, never used as a page URL.
      meta: { title: translatePageText('订购'), icon: 'lucide-shopping-cart' },
      alwaysShow: true,
      children
    }
    const grouped = new Set(orderModules)
    const first = visibleModules.find(item => grouped.has(item))
    return visibleModules.flatMap(item => item === first ? [group] : grouped.has(item) ? [] : [item])
  })
  const accountDetail = computed(() => accountDetailNavigation(route.path))
  const activePath = computed(() => accountDetail.value ? '/users-and-accounts' : String(route.meta.activeMenu || route.path))
  function score(menu: any, target: string): number {
    const path = menu.path
    const own = !/^https?:/.test(path) && (target === path || (path !== '/' && target.startsWith(path + '/'))) ? path.length : -1
    return Math.max(own, ...menu.children.map((child: any) => score(child, target)))
  }
  const activeModule = computed(() => {
    let match: any
    let best = -1
    for (const menu of modules.value) {
      // activeMenu links hidden detail routes back to their visible menu.
      const value = score(menu, activePath.value)
      if (value > best) { best = value; match = menu }
    }
    if (match) return match
    return modules.value.find(menu => route.matched.some(record => record.path === menu.path))
  })
  const accountMenus = computed(() => {
    const teachingOptions = [
      ['organization:teacher:list', 'teachers'],
      ['organization:user:list', 'studentAccounts'],
      ['organization:export:list', 'exports']
    ]
    const teaching = teachingOptions.find(([permission]) => checkPermi([permission]))
    const entries = []
    if (teaching) entries.push({ title: '教学账号', tab: teaching[1], key: 'teaching', icon: 'lucide-graduation-cap' })
    if (checkPermi(['organization:user:list'])) entries.push({ title: 'Domi用户', tab: 'userProfiles', key: 'consumer', icon: 'lucide-user-round' })
    if (checkPermi(['organization:student:list'])) entries.push({ title: '机构学生', tab: 'cadets', key: 'cadets', icon: 'lucide-graduation-cap' })
    return entries.map(entry => ({
      path: '/users-and-accounts',
      query: { ...route.query, tab: entry.tab },
      meta: { title: translatePageText(entry.title), icon: entry.icon, menuIndex: `account-${entry.key}` },
      children: []
    }))
  })
  const sidebarActivePath = computed(() => {
    if (route.path !== '/users-and-accounts' && !accountDetail.value) return activePath.value
    const personal = accountDetail.value ? accountDetail.value.category === 'consumer' : ['userProfiles', 'user-profiles'].includes(String(route.query.tab))
    const preferred = route.path === '/users-and-accounts' && route.query.tab === 'cadets'
      ? 'account-cadets'
      : personal ? 'account-consumer' : 'account-teaching'
    return accountMenus.value.find(item => item.meta.menuIndex === preferred)?.meta.menuIndex || accountMenus.value[0]?.meta.menuIndex || activePath.value
  })
  const sidebarMenus = computed(() => {
    if ((route.path === '/users-and-accounts' || accountDetail.value) && activeModule.value) return accountMenus.value
    const menu = activeModule.value
    const children = (menu?.children || []).filter((item: any) => !item.hidden)
    if (menu?.path !== '/course') return children

    // Group only the existing, permitted institution entries. Official menus stay separate.
    const institutionTitles = new Set(['课件列表', '课包列表', '课件分类', '课包分类'])
    const institutionMenus = children.filter((item: any) => institutionTitles.has(item.meta?.title))
    if (!institutionMenus.length) return children

    const group = {
      path: '/course/__institution-menu', // Submenu index only; never registered as a page route.
      alwaysShow: true,
      meta: { title: translatePageText('机构课程'), icon: 'lucide-book-open' },
      children: institutionMenus
    }
    const grouped = new Set(institutionMenus)
    return children.flatMap((item: any) => {
      if (item === institutionMenus[0]) return [group]
      return grouped.has(item) ? [] : [item]
    })
  })
  const hasSidebar = computed(() => sidebarMenus.value.length > 0)
  return { modules, activeModule, activePath: sidebarActivePath, sidebarMenus, hasSidebar }
}
